
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import Hero from "./home/Hero";
import Crew from "./home/Crew";
import { REVIEWS } from "../data";
import LogoStrip from "./home/LogoStrip";
import LatestBlog from "./home/LatestBlog";
import AboutStats from "./home/AboutStats";
import Testimonials from "./home/Testimonials";
import ManagerModal from "./common/ManagerModal";
import EventsAccordion from "./home/EventsAccordion";
import FeaturedTournaments from "./home/FeaturedTournaments";
import { hostBanner, hostLogo, isPageVisible, pagePath } from "../helpers";
import { ApiResponseData, BaseService, IHostBlogArticle, IHostManager, IHostWebsite, ITournament, IWebsiteEvent, OmneHostService, SanitizedResponse } from "../../../../services";

export interface HomeProps {
    basePath: string;
    website: IHostWebsite;
}

function Home({ website, basePath }: HomeProps) {

    const navigate = ReactRouterDom.useNavigate();
    const settings = website.home_settings ?? {} as IHostWebsite["home_settings"];
    const [events, setEvents] = React.useState<IWebsiteEvent[]>();
    const [articles, setArticles] = React.useState<IHostBlogArticle[]>();
    const [tournaments, setTournaments] = React.useState<ITournament[]>();
    const [managers, setManagers] = React.useState<ApiResponseData<IHostManager>>();
    const [selectedManager, setSelectedManager] = React.useState<IHostManager | null>(null);
    const crew = (managers?.content ?? []).filter((m) => !m.settings?.hide_from_managers_page);
    const showTournaments =isPageVisible(website, "TOURNAMENTS") && settings.show_top_tournaments !== false;
    const heroImages = [
        hostBanner(website),
        ...(tournaments ?? []).map((t) => t.tournament_edition?.banner_url),
        ...(events ?? []).map((e) => e.media_location),
        ...(articles ?? []).map((a) => a.extra_data?.featured_image),
        hostLogo(website),
    ].filter((src, i, all): src is string => !!src && all.indexOf(src) === i);

    React.useEffect(fetchInformation, [website.host.external_id]);

    return (<>
        <Hero website={website} basePath={basePath} images={heroImages} />
        {(settings.show_sponsors && !!website.sponsors?.length) && (<LogoStrip logos={website.sponsors} />)}
        <AboutStats website={website} teamCount={managers?.total_elements} />
        {(showTournaments && !!tournaments?.length) && (<FeaturedTournaments basePath={basePath} tournaments={tournaments} onSelect={(t) => navigate({
            pathname: pagePath(basePath, "TOURNAMENTS"),
            search: "tournament=" + t.external_id,
        })} />)}
        {(isPageVisible(website, "EVENTS") && !!events?.length) && (<EventsAccordion basePath={basePath} events={events} />)}
        {(isPageVisible(website, "ABOUT") && !!crew.length) && (<Crew basePath={basePath} managers={crew} onSelect={setSelectedManager} />)}
        {settings.show_top_best_tournament_reviews !== false && (<Testimonials reviews={REVIEWS} />)}
        {(isPageVisible(website, "BLOG") && !!articles?.length) && (<LatestBlog basePath={basePath} articles={articles} onSelect={(a) => navigate({
            pathname: pagePath(basePath, "BLOG"),
            search: "article=" + a.external_id,
        })} />)}
        {selectedManager && (<ManagerModal manager={selectedManager} onClose={() => setSelectedManager(null)} />)}
    </>);

    function fetchInformation() {
        const omnehostService = OmneHostService.getInstance();
        const hostId = website.host.external_id;
        omnehostService.queryHostTournaments(hostId, {
            size: 8,
            sort: "public_id,DESC",
        }).then(({ content }: SanitizedResponse<ITournament>) => {
            setTournaments((content ?? []).filter((t) => !!t.tournament_edition));
        }).catch(BaseService.reportError);
        omnehostService.queryHostEvents(hostId, {
            size: 5,
            sort: "start_date,DESC",
        }).then(({ content }: SanitizedResponse<IWebsiteEvent>) => {
            setEvents(content ?? []);
        }).catch(BaseService.reportError);
        omnehostService.queryHostBlogArticles(hostId, {
            size: 4,
            sort: "updated_at,DESC",
        }).then(({ content }: SanitizedResponse<IHostBlogArticle>) => {
            setArticles(content ?? []);
        }).catch(BaseService.reportError);
        omnehostService.queryHostManagers(hostId, {
            size: 4,
            sort: "role,DESC",
        }).then(({ apiResponse }: SanitizedResponse<IHostManager>) => {
            setManagers(apiResponse.data ?? {});
        }).catch(BaseService.reportError);
    }
}

export default Home;
